import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Loader2, Shield, Search, ChevronLeft, ChevronRight, 
  User, Calendar, Clock, Crown, Ban, CheckCircle, XCircle,
  AlertTriangle, X, RefreshCw
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import {
  adminListUsers,
  adminGetUser,
  adminToggleDisabled,
  adminToggleCircleOverride,
  adminUpdateRoles,
  adminGetUserAudit,
  adminResyncStripe,
  getAdminMe
} from '../services/api';

// Toast notification component
const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className={`fixed bottom-4 right-4 px-4 py-3 rounded-lg shadow-lg z-50 flex items-center gap-2 ${
      type === 'success' ? 'bg-forest text-white' : 'bg-red-500 text-white'
    }`}>
      {type === 'success' ? <CheckCircle className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
      <span>{message}</span>
    </div>
  );
};

// Confirmation Modal
const ConfirmationModal = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  title, 
  description, 
  confirmWord,
  actionLabel,
  isDestructive = false 
}) => {
  const [inputValue, setInputValue] = useState('');
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleConfirm = async () => {
    if (inputValue.toUpperCase() !== confirmWord.toUpperCase()) return;
    if (!reason.trim()) return;
    
    setIsSubmitting(true);
    try {
      await onConfirm(reason);
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setInputValue('');
    setReason('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-fern-card rounded-2xl p-6 max-w-md w-full mx-4 border border-calm-border shadow-xl">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-calm-text" style={{fontFamily: 'DM Serif Display, serif'}}>
            {title}
          </h3>
          <button onClick={handleClose} className="text-calm-text-secondary hover:text-calm-text">
            <X className="h-5 w-5" />
          </button>
        </div>
        
        <p className="text-calm-text-secondary text-sm mb-4">{description}</p>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-calm-text mb-1">
              Type &quot;{confirmWord}&quot; to confirm
            </label>
            <Input
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder={confirmWord}
              className="bg-fern border-calm-border text-calm-text"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-calm-text mb-1">
              Reason (required)
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Explain why this action is being taken..."
              className="w-full px-3 py-2 bg-fern border border-calm-border rounded-md text-calm-text placeholder:text-calm-text-secondary resize-none"
              rows={3}
            />
          </div>
          
          <div className="flex gap-3 pt-2">
            <Button
              variant="outline"
              onClick={handleClose}
              className="flex-1 border-calm-border text-calm-text hover:bg-fern"
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirm}
              disabled={inputValue.toUpperCase() !== confirmWord.toUpperCase() || !reason.trim() || isSubmitting}
              className={`flex-1 ${isDestructive ? 'bg-red-500 hover:bg-red-600' : 'bg-forest hover:bg-forest-dark'} text-white`}
            >
              {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : actionLabel}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// User Detail Drawer
const UserDetailDrawer = ({ userId, onClose, onUpdate, showToast }) => {
  const [user, setUser] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [modal, setModal] = useState({ isOpen: false, type: null });

  const loadUserDetail = useCallback(async () => {
    try {
      const data = await adminGetUser(userId);
      setUser(data);
    } catch (error) {
      console.error('Failed to load user:', error);
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  const loadAuditLog = useCallback(async () => {
    try {
      const data = await adminGetUserAudit(userId);
      setAuditLogs(data.audit_logs || []);
    } catch (error) {
      console.error('Failed to load audit log:', error);
    }
  }, [userId]);

  useEffect(() => {
    if (userId) {
      loadUserDetail();
      loadAuditLog();
    }
  }, [userId, loadUserDetail, loadAuditLog]);

  const handleDisableToggle = async (reason) => {
    const newDisabled = !user.user.disabled;
    await adminToggleDisabled(userId, newDisabled, reason);
    showToast('Updated.', 'success');
    loadUserDetail();
    loadAuditLog();
    onUpdate();
  };

  const handleCircleOverrideToggle = async (reason) => {
    const newOverride = !user.user.circle_override;
    await adminToggleCircleOverride(userId, newOverride, reason);
    showToast('Updated.', 'success');
    loadUserDetail();
    loadAuditLog();
    onUpdate();
  };

  const handleRoleChange = async (role, action, reason) => {
    const add = action === 'grant' ? [role] : null;
    const remove = action === 'revoke' ? [role] : null;
    try {
      await adminUpdateRoles(userId, add, remove, reason);
      showToast('Updated.', 'success');
      loadUserDetail();
      loadAuditLog();
      onUpdate();
    } catch (error) {
      showToast(error.message, 'error');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString();
  };

  if (isLoading) {
    return (
      <div className="fixed inset-y-0 right-0 w-full md:w-[500px] bg-fern-card border-l border-calm-border shadow-xl z-40 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-forest" />
      </div>
    );
  }

  if (!user) return null;

  const { user: userData, subscription, has_profile } = user;

  return (
    <>
      <div className="fixed inset-y-0 right-0 w-full md:w-[500px] bg-fern-card border-l border-calm-border shadow-xl z-40 overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-fern-card border-b border-calm-border p-4 flex items-center justify-between">
          <h2 className="text-lg font-medium text-calm-text" style={{fontFamily: 'DM Serif Display, serif'}}>
            User Detail
          </h2>
          <button onClick={onClose} className="text-calm-text-secondary hover:text-calm-text">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-4 space-y-6">
          {/* Identity Section */}
          <section>
            <h3 className="text-sm font-medium text-calm-text-secondary uppercase tracking-wide mb-3">Identity</h3>
            <div className="bg-herbal-light/30 rounded-xl p-4 space-y-2">
              <div className="flex justify-between">
                <span className="text-calm-text-secondary text-sm">User ID</span>
                <span className="text-forest font-mono text-sm">{userData.user_id}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-calm-text-secondary text-sm">Email</span>
                <span className="text-forest text-sm">{userData.email}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-calm-text-secondary text-sm">Created</span>
                <span className="text-forest text-sm">{formatDate(userData.created_at)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-calm-text-secondary text-sm">Last Seen</span>
                <span className="text-forest text-sm">{formatDate(userData.last_seen)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-calm-text-secondary text-sm">Roles</span>
                <div className="flex gap-1">
                  {userData.roles?.map(role => (
                    <span key={role} className={`px-2 py-0.5 text-xs rounded-full ${
                      role === 'admin' ? 'bg-purple-100 text-purple-700' :
                      role === 'support' ? 'bg-blue-100 text-blue-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {role}
                    </span>
                  ))}
                </div>
              </div>
              <div className="flex justify-between">
                <span className="text-calm-text-secondary text-sm">Has Profile</span>
                <span className="text-forest text-sm">{has_profile ? 'Yes' : 'No'}</span>
              </div>
            </div>
          </section>

          {/* Access Controls */}
          <section>
            <h3 className="text-sm font-medium text-calm-text-secondary uppercase tracking-wide mb-3">Access Controls</h3>
            <div className="space-y-3">
              {/* Account Disabled Toggle */}
              <div className="bg-herbal-light/30 rounded-xl p-4 flex items-center justify-between">
                <div>
                  <p className="text-calm-text font-medium">Account Disabled</p>
                  <p className="text-calm-text-secondary text-xs">
                    {userData.disabled ? 'User cannot sign in' : 'User has normal access'}
                  </p>
                </div>
                <Button
                  onClick={() => setModal({ 
                    isOpen: true, 
                    type: 'disable',
                    confirmWord: userData.disabled ? 'ENABLE' : 'DISABLE',
                    title: userData.disabled ? 'Enable Account' : 'Disable Account',
                    description: userData.disabled 
                      ? 'This will restore the user\'s ability to sign in.'
                      : 'This will prevent the user from signing in via magic link.',
                    actionLabel: userData.disabled ? 'Enable' : 'Disable',
                    isDestructive: !userData.disabled,
                    onConfirm: handleDisableToggle
                  })}
                  variant={userData.disabled ? 'default' : 'outline'}
                  size="sm"
                  className={userData.disabled ? 'bg-forest text-white' : 'border-red-300 text-red-600 hover:bg-red-50'}
                >
                  {userData.disabled ? <CheckCircle className="h-4 w-4 mr-1" /> : <Ban className="h-4 w-4 mr-1" />}
                  {userData.disabled ? 'Enable' : 'Disable'}
                </Button>
              </div>

              {/* Circle Override Toggle */}
              <div className="bg-herbal-light/30 rounded-xl p-4 flex items-center justify-between">
                <div>
                  <p className="text-calm-text font-medium">Circle Override</p>
                  <p className="text-calm-text-secondary text-xs">
                    {userData.circle_override ? 'Circle features granted manually' : 'Circle status from Stripe'}
                  </p>
                </div>
                <Button
                  onClick={() => setModal({ 
                    isOpen: true, 
                    type: 'circle',
                    confirmWord: userData.circle_override ? 'REVOKE' : 'GRANT',
                    title: userData.circle_override ? 'Revoke Circle Override' : 'Grant Circle Override',
                    description: userData.circle_override 
                      ? 'This will return the user to Stripe-driven Circle status.'
                      : 'This will grant Circle features regardless of Stripe subscription.',
                    actionLabel: userData.circle_override ? 'Revoke' : 'Grant',
                    isDestructive: userData.circle_override,
                    onConfirm: handleCircleOverrideToggle
                  })}
                  variant={userData.circle_override ? 'default' : 'outline'}
                  size="sm"
                  className={userData.circle_override ? 'bg-forest text-white' : 'border-calm-border text-forest hover:bg-fern'}
                >
                  <Crown className="h-4 w-4 mr-1" />
                  {userData.circle_override ? 'Revoke' : 'Grant'}
                </Button>
              </div>
            </div>
          </section>

          {/* Subscription Snapshot */}
          <section>
            <h3 className="text-sm font-medium text-calm-text-secondary uppercase tracking-wide mb-3">Subscription</h3>
            <div className="bg-herbal-light/30 rounded-xl p-4 space-y-2">
              {subscription ? (
                <>
                  <div className="flex justify-between">
                    <span className="text-calm-text-secondary text-sm">Status</span>
                    <span className={`px-2 py-0.5 text-xs rounded-full ${
                      subscription.status === 'active' ? 'bg-green-100 text-green-700' :
                      subscription.status === 'past_due' ? 'bg-yellow-100 text-yellow-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {subscription.status}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-calm-text-secondary text-sm">Plan</span>
                    <span className="text-forest text-sm">{subscription.plan || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-calm-text-secondary text-sm">Period End</span>
                    <span className="text-forest text-sm">{formatDate(subscription.current_period_end)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-calm-text-secondary text-sm">Cancel at Period End</span>
                    <span className="text-forest text-sm">{subscription.cancel_at_period_end ? 'Yes' : 'No'}</span>
                  </div>
                  {subscription.stripe_customer_id && (
                    <div className="flex justify-between">
                      <span className="text-calm-text-secondary text-sm">Stripe Customer</span>
                      <span className="text-forest font-mono text-xs">{subscription.stripe_customer_id}</span>
                    </div>
                  )}
                  {subscription.resynced_at && (
                    <div className="flex justify-between">
                      <span className="text-calm-text-secondary text-sm">Last Resynced</span>
                      <span className="text-calm-text-secondary text-xs">{formatDate(subscription.resynced_at)}</span>
                    </div>
                  )}
                </>
              ) : (
                <p className="text-calm-text-secondary text-sm">No subscription</p>
              )}
              
              {/* Resync from Stripe Button */}
              <div className="pt-3 border-t border-calm-border/50">
                <Button
                  onClick={async () => {
                    setIsActionLoading(true);
                    try {
                      const result = await adminResyncStripe(userData.user_id);
                      showToast(`${result.message}`, 'success');
                      // Refresh user data using the existing function
                      loadUserDetail();
                      loadAuditLog();
                      onUpdate();
                    } catch (err) {
                      showToast(err.message || 'Failed to resync', 'error');
                    } finally {
                      setIsActionLoading(false);
                    }
                  }}
                  disabled={isActionLoading}
                  variant="outline"
                  className="w-full border-calm-border text-forest hover:bg-fern text-sm"
                >
                  {isActionLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <RefreshCw className="h-4 w-4 mr-2" />
                  )}
                  Resync from Stripe
                </Button>
                <p className="text-xs text-calm-text-secondary mt-2 text-center">
                  Fetches latest subscription status directly from Stripe
                </p>
              </div>
            </div>
          </section>

          {/* Role Management */}
          <section>
            <h3 className="text-sm font-medium text-calm-text-secondary uppercase tracking-wide mb-3">Role Management</h3>
            <div className="bg-herbal-light/30 rounded-xl p-4 space-y-2">
              {/* Admin Role */}
              <div className="flex items-center justify-between">
                <span className="text-calm-text text-sm">Admin</span>
                <Button
                  onClick={() => {
                    const hasAdmin = userData.roles?.includes('admin');
                    setModal({ 
                      isOpen: true, 
                      type: 'role',
                      confirmWord: 'CONFIRM',
                      title: hasAdmin ? 'Revoke Admin Role' : 'Grant Admin Role',
                      description: hasAdmin 
                        ? 'This will remove admin privileges from this user.'
                        : 'This will grant full admin access to this user.',
                      actionLabel: hasAdmin ? 'Revoke' : 'Grant',
                      isDestructive: hasAdmin,
                      onConfirm: (reason) => handleRoleChange('admin', hasAdmin ? 'revoke' : 'grant', reason)
                    });
                  }}
                  variant="outline"
                  size="sm"
                  className="border-calm-border text-forest hover:bg-fern"
                >
                  {userData.roles?.includes('admin') ? 'Revoke' : 'Grant'}
                </Button>
              </div>
              {/* Support Role */}
              <div className="flex items-center justify-between">
                <span className="text-calm-text text-sm">Support</span>
                <Button
                  onClick={() => {
                    const hasSupport = userData.roles?.includes('support');
                    setModal({ 
                      isOpen: true, 
                      type: 'role',
                      confirmWord: 'CONFIRM',
                      title: hasSupport ? 'Revoke Support Role' : 'Grant Support Role',
                      description: hasSupport 
                        ? 'This will remove support privileges from this user.'
                        : 'This will grant support access to this user.',
                      actionLabel: hasSupport ? 'Revoke' : 'Grant',
                      isDestructive: hasSupport,
                      onConfirm: (reason) => handleRoleChange('support', hasSupport ? 'revoke' : 'grant', reason)
                    });
                  }}
                  variant="outline"
                  size="sm"
                  className="border-calm-border text-forest hover:bg-fern"
                >
                  {userData.roles?.includes('support') ? 'Revoke' : 'Grant'}
                </Button>
              </div>
            </div>
          </section>

          {/* Audit Log */}
          <section>
            <h3 className="text-sm font-medium text-calm-text-secondary uppercase tracking-wide mb-3">Audit Log</h3>
            <div className="bg-herbal-light/30 rounded-xl p-4">
              {auditLogs.length > 0 ? (
                <div className="space-y-3">
                  {auditLogs.map((log, index) => (
                    <div key={index} className="border-b border-calm-border/50 pb-3 last:border-0 last:pb-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`px-2 py-0.5 text-xs rounded-full ${
                          log.action.includes('disabled') || log.action.includes('revoked') 
                            ? 'bg-red-100 text-red-700' 
                            : 'bg-green-100 text-green-700'
                        }`}>
                          {log.action.replace(/_/g, ' ')}
                        </span>
                        <span className="text-xs text-calm-text-secondary">
                          {new Date(log.timestamp).toLocaleString()}
                        </span>
                      </div>
                      <p className="text-xs text-calm-text">
                        By: {log.actor_email}
                      </p>
                      <p className="text-xs text-calm-text-secondary mt-1">
                        {log.reason}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-calm-text-secondary text-sm">No audit events</p>
              )}
            </div>
          </section>
        </div>
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={modal.isOpen}
        onClose={() => setModal({ isOpen: false })}
        onConfirm={modal.onConfirm}
        title={modal.title}
        description={modal.description}
        confirmWord={modal.confirmWord}
        actionLabel={modal.actionLabel}
        isDestructive={modal.isDestructive}
      />
    </>
  );
};

// Main Admin Page
const Admin = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminUser, setAdminUser] = useState(null);
  const [users, setUsers] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 0 });
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState('');
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [toast, setToast] = useState(null);

  const loadUsers = useCallback(async () => {
    try {
      const data = await adminListUsers(searchQuery, activeFilter, pagination.page, pagination.limit);
      setUsers(data.users || []);
      setPagination(prev => ({ ...prev, ...data.pagination }));
    } catch (error) {
      console.error('Failed to load users:', error);
    }
  }, [searchQuery, activeFilter, pagination.page, pagination.limit]);

  const checkAdminAccess = useCallback(async () => {
    try {
      const token = localStorage.getItem('ohmnai_session_token');
      if (!token) {
        navigate('/', { replace: true });
        return;
      }

      const data = await getAdminMe();
      setAdminUser(data);
      setIsAdmin(true);
    } catch (error) {
      navigate('/', { replace: true });
    } finally {
      setIsLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    checkAdminAccess();
  }, [checkAdminAccess]);

  useEffect(() => {
    if (isAdmin) {
      loadUsers();
    }
  }, [isAdmin, loadUsers]);

  // Debounced search
  useEffect(() => {
    if (!isAdmin) return;
    const timer = setTimeout(() => {
      loadUsers();
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, isAdmin, loadUsers]);

  const showToast = (message, type) => {
    setToast({ message, type });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString();
  };

  const filters = [
    { id: '', label: 'All' },
    { id: 'circle_any', label: 'Circle' },
    { id: 'paid_active', label: 'Paid' },
    { id: 'override', label: 'Granted' },
    { id: 'disabled', label: 'Disabled' },
    { id: 'admins', label: 'Admins' }
  ];

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-mist via-mist-light to-fern flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-forest" />
      </div>
    );
  }

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-mist via-mist-light to-fern">
      {/* Header */}
      <header className="border-b border-calm-border bg-fern-card/80 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div 
              className="text-2xl font-bold text-calm-text cursor-pointer" 
              style={{fontFamily: 'DM Serif Display, serif'}}
              onClick={() => navigate('/')}
            >
              OHMNAI
            </div>
            <div className="flex items-center gap-2 px-3 py-1 bg-forest/10 rounded-full">
              <Shield className="h-4 w-4 text-forest" />
              <span className="text-sm font-medium text-forest">Admin</span>
            </div>
          </div>
          <div className="text-sm text-calm-text-secondary">
            {adminUser?.email}
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="bg-fern-card rounded-2xl border border-calm-border shadow-lg overflow-hidden">
          {/* Title & Search */}
          <div className="p-6 border-b border-calm-border">
            <h1 className="text-2xl text-calm-text mb-4" style={{fontFamily: 'DM Serif Display, serif'}}>
              Users
            </h1>
            
            <div className="flex flex-col sm:flex-row gap-4">
              {/* Search */}
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-calm-text-secondary" />
                <Input
                  type="text"
                  placeholder="Search email or user id..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 bg-fern border-calm-border text-calm-text placeholder:text-calm-text-secondary"
                />
              </div>
              
              {/* Filter Chips */}
              <div className="flex flex-wrap gap-2">
                {filters.map(filter => (
                  <button
                    key={filter.id}
                    onClick={() => {
                      setActiveFilter(filter.id);
                      setPagination(prev => ({ ...prev, page: 1 }));
                    }}
                    className={`px-3 py-1.5 text-sm rounded-full transition-colors ${
                      activeFilter === filter.id
                        ? 'bg-forest text-white'
                        : 'bg-herbal-light/50 text-forest hover:bg-herbal-light'
                    }`}
                  >
                    {filter.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-herbal-light/30">
                <tr>
                  <th className="text-left px-6 py-3 text-xs font-medium text-calm-text-secondary uppercase tracking-wider">Email</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-calm-text-secondary uppercase tracking-wider">Roles</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-calm-text-secondary uppercase tracking-wider">Account</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-calm-text-secondary uppercase tracking-wider">Circle</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-calm-text-secondary uppercase tracking-wider">Subscription</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-calm-text-secondary uppercase tracking-wider">Last Seen</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-calm-text-secondary uppercase tracking-wider">Created</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-calm-border/50">
                {users.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-12 text-center text-calm-text-secondary">
                      No users found.
                    </td>
                  </tr>
                ) : (
                  users.map(user => (
                    <tr 
                      key={user.user_id} 
                      onClick={() => setSelectedUserId(user.user_id)}
                      className="hover:bg-herbal-light/20 cursor-pointer transition-colors"
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-calm-text-secondary" />
                          <span className="text-forest text-sm">{user.email}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex gap-1">
                          {user.roles?.map(role => (
                            <span key={role} className={`px-2 py-0.5 text-xs rounded-full ${
                              role === 'admin' ? 'bg-purple-100 text-purple-700' :
                              role === 'support' ? 'bg-blue-100 text-blue-700' :
                              'bg-gray-100 text-gray-700'
                            }`}>
                              {role}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-0.5 text-xs rounded-full ${
                          user.disabled ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
                        }`}>
                          {user.disabled ? 'Disabled' : 'Active'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-0.5 text-xs rounded-full ${
                          user.circle_status === 'None' ? 'bg-gray-100 text-gray-700' :
                          user.circle_status === 'Paid' ? 'bg-green-100 text-green-700' :
                          user.circle_status === 'Granted' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-emerald-100 text-emerald-700'
                        }`}>
                          {user.circle_status}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-0.5 text-xs rounded-full ${
                          user.subscription?.status === 'active' ? 'bg-green-100 text-green-700' :
                          user.subscription?.status === 'past_due' ? 'bg-yellow-100 text-yellow-700' :
                          user.subscription?.status === 'canceled' ? 'bg-gray-100 text-gray-700' :
                          'bg-gray-100 text-gray-500'
                        }`}>
                          {user.subscription?.status || 'none'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-calm-text-secondary">
                        {formatDate(user.last_seen)}
                      </td>
                      <td className="px-6 py-4 text-sm text-calm-text-secondary">
                        {formatDate(user.created_at)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="px-6 py-4 border-t border-calm-border flex items-center justify-between">
              <p className="text-sm text-calm-text-secondary">
                Page {pagination.page} of {pagination.pages} ({pagination.total} users)
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                  disabled={pagination.page <= 1}
                  className="border-calm-border text-forest hover:bg-fern"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                  disabled={pagination.page >= pagination.pages}
                  className="border-calm-border text-forest hover:bg-fern"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* User Detail Drawer */}
      {selectedUserId && (
        <>
          <div 
            className="fixed inset-0 bg-black/30 z-30"
            onClick={() => setSelectedUserId(null)}
          />
          <UserDetailDrawer
            userId={selectedUserId}
            onClose={() => setSelectedUserId(null)}
            onUpdate={loadUsers}
            showToast={showToast}
          />
        </>
      )}

      {/* Toast */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default Admin;
